import { engineComponent, NumberUtils, Utils } from "neo-common";
import { Communication } from "neo-games";
import { BaseController } from "./BaseController";
import { Paylines } from "../Paylines";
import { Symbols } from "../Symbols";
import { WinAnimator } from "../WinAnimator";
import { Machine } from "../Machine";
import { SwitchElement } from "../../Components/Elements/Graphics/SwitchElement";
import { ContainerElement } from "../../Components/Elements/Graphics/ContainerElement";
import { ComponentTypes } from "../../Components/ComponentTypes";
import { WinAnimatorExtended } from "../../CustomComponents/WinAnimatorExtended";
import { GameManager } from "../../Managers/GameManager";
import { FrameTimerEventEmitter, CreateFrameTimer, FrameTimerEvents } from "../../Utils/FrameTimer";
import { PlayList } from "../../Components/Sounds/PlayList";
import { Tweens } from "../../Components/Effects/Tweens";
import { ImageElement } from "../../Components/Elements/Graphics/ImageElement";
import { ModuleManager } from "../../Managers/ModuleManager";
import { FreespinsView } from "../Freespins/FreespinsView";
import { StageController } from "../../Components/StageController";
import { DataManager } from "../../Managers/DataManager";
import { SceneHolderElement } from "../../CustomComponents/SceneHolderElement";
import { LayoutContainerElement } from "../../CustomComponents/LayoutContainerElement";
import { LayoutManager } from "../../Managers/LayoutManager";
import { LayoutSwitchElement } from "../../CustomComponents/LayoutSwitchElement";
import { ImageTextCounterElement } from "../../Components/Elements/Texts/ImageTextCounterElement";
import { ImageTextElement } from "../../Components/Elements/Texts/ImageTextElement";
import { BetManager } from "../../Managers/BetManager";
import { CustomUtils } from "../../Utils/CustomUtils";
import { HtmlController } from "../../HTMLComponents/HtmlController";

type IWin = Communication.Game.IWin;

@engineComponent(ComponentTypes.DisplayObject)
export class BaseView extends LayoutContainerElement {
    public static LayoutManager: LayoutManager;

    private _controller: BaseController;
    private _wasFinished: boolean = false;
    private _introDelay: FrameTimerEventEmitter;
    private _outroDelay: FrameTimerEventEmitter;
    public static _progressBarS5: ContainerElement;
    public static _multiplierS5: LayoutSwitchElement;
    public static _progressBarS3: ContainerElement;
    public static _multiplierS3: LayoutSwitchElement;
    public static _progressBarS1: ContainerElement;
    public static _multiplierS1: LayoutSwitchElement;
    public static _progressBarS6: ContainerElement;
    public static _multiplierS6: LayoutSwitchElement;
    public static _progressBarS4: ContainerElement;
    public static _multiplierS4: LayoutSwitchElement;
    public static _progressBarS2: ContainerElement;
    public static _multiplierS2: LayoutSwitchElement;

    public static _isMultipliedS1: boolean = false;
    public static _isMultipliedS2: boolean = false;
    public static _isMultipliedS3: boolean = false;
    public static _isMultipliedS4: boolean = false;
    public static _isMultipliedS5: boolean = false;
    public static _isMultipliedS6: boolean = false;
    public static _isMultipliedTwiceS1: boolean = false;
    public static _isMultipliedTwiceS2: boolean = false;
    public static _isMultipliedTwiceS3: boolean = false;
    public static _isMultipliedTwiceS4: boolean = false;
    public static _isMultipliedTwiceS5: boolean = false;
    public static _isMultipliedTwiceS6: boolean = false;

    public static _isInitIntro: boolean = false;

    public static messages: LayoutSwitchElement;
    private _fsMessageTimer: FrameTimerEventEmitter;

    public static _multiplierTimer: FrameTimerEventEmitter;
    public static _multiplierTwiceTimer: FrameTimerEventEmitter;
    public static _progressBarTimer: FrameTimerEventEmitter;
    public static _progressBarTwiceTimer: FrameTimerEventEmitter;

    public static customCollectionBarsData: any;
    public static customCollectionBarsDataPreviousSpin: any;
    public static customCollectionBarsDataPreviousSpinBonus: any;

    private _paylines: Paylines;

    private _symbols: Symbols;
    private _machine: Machine;
    private _noWinContainer: ContainerElement;
    private _symbolsContainer: ContainerElement;

    private _winAnimator: WinAnimatorExtended;

    private _teasing: SwitchElement;
    private _background: SwitchElement;

    private _textContainerBottom: HTMLElement;
    private _textContainer: HTMLElement;

    private _logo: ImageElement;

    private static readonly GambleInTransitionDuration: number = 30; //in frames
    private _gambleTransition: SwitchElement;
    private _gambleTransitionSceneHolder: SceneHolderElement;

    protected currentBet: number = -1;

    constructor() {
        super();

        this._introDelay = CreateFrameTimer("introDelay");
        this._introDelay.on(FrameTimerEvents.Complete, this.introFinished.bind(this));

        this._outroDelay = CreateFrameTimer("outroDelay");
        this._outroDelay.on(FrameTimerEvents.Complete, this.outroFinished.bind(this));

        this._fsMessageTimer = CreateFrameTimer("fsMessageDelay");
        this._fsMessageTimer.on(FrameTimerEvents.Complete, this.showFsMessage.bind(this));

        BaseView._multiplierTimer = CreateFrameTimer("multiplierTimer");
        BaseView._multiplierTwiceTimer = CreateFrameTimer("multiplierTwiceTimer");
        BaseView._progressBarTimer = CreateFrameTimer("progressBarTimer");
        BaseView._progressBarTwiceTimer = CreateFrameTimer("progressBarTwiceTimer");

        StageController.baseStageContainer = this;
    }

    create(node: Element): void {
        super.create(node);

        BaseView.LayoutManager = new LayoutManager();

        if (this.childs != null) {
            for (var key in this.childs) {
                if (this.childs[key] instanceof Paylines) {
                    this._paylines = this.childs[key];
                }

                if (key === "GambleTransition" && this.childs[key] instanceof SwitchElement) {
                    this._gambleTransition = this.childs[key];
                    this._gambleTransitionSceneHolder = <SceneHolderElement>(
                        this._gambleTransition.getChildElementByName("Scene")
                    );
                }

                if (this.childs[key] instanceof Symbols) {
                    this._symbols = this.childs[key];
                    this.removeChild(this._symbols);
                }

                if (this.childs[key] instanceof Machine) {
                    this._machine = this.childs[key];
                }

                if (this.childs[key] instanceof WinAnimator) {
                    this._winAnimator = this.childs[key];
                }

                if (key === "NoWinContainer" && this.childs[key] instanceof ContainerElement) {
                    this._noWinContainer = this.childs[key];
                }

                if (key === "SymbolsContainer" && this.childs[key] instanceof ContainerElement) {
                    this._symbolsContainer = this.childs[key];
                }

                if (key === "Teasing" && this.childs[key] instanceof SwitchElement) {
                    this._teasing = this.childs[key];
                }

                if (key === "Background" && this.childs[key] instanceof SwitchElement) {
                    this._background = this.childs[key];
                }

                if (key === "Logo" && this.childs[key] instanceof ImageElement) {
                    this._logo = this.childs[key];
                }

                if (key === "Messages" && this.childs[key] instanceof LayoutSwitchElement) {
                    BaseView.messages = this.childs[key];
                }

                if (key === "MultiplierS1" && this.childs[key] instanceof LayoutSwitchElement) {
                    BaseView._multiplierS1 = this.childs[key];
                    BaseView._progressBarS1 = BaseView._multiplierS1.getChildElementByName(
                        "ProgressBar",
                    ) as ContainerElement;
                }

                if (key === "MultiplierS2" && this.childs[key] instanceof LayoutSwitchElement) {
                    BaseView._multiplierS2 = this.childs[key];
                    BaseView._progressBarS2 = BaseView._multiplierS2.getChildElementByName(
                        "ProgressBar",
                    ) as ContainerElement;
                }

                if (key === "MultiplierS3" && this.childs[key] instanceof LayoutSwitchElement) {
                    BaseView._multiplierS3 = this.childs[key];
                    BaseView._progressBarS3 = BaseView._multiplierS3.getChildElementByName(
                        "ProgressBar",
                    ) as ContainerElement;
                }

                if (key === "MultiplierS4" && this.childs[key] instanceof LayoutSwitchElement) {
                    BaseView._multiplierS4 = this.childs[key];
                    BaseView._progressBarS4 = BaseView._multiplierS4.getChildElementByName(
                        "ProgressBar",
                    ) as ContainerElement;
                }

                if (key === "MultiplierS5" && this.childs[key] instanceof LayoutSwitchElement) {
                    BaseView._multiplierS5 = this.childs[key];
                    BaseView._progressBarS5 = BaseView._multiplierS5.getChildElementByName(
                        "ProgressBar",
                    ) as ContainerElement;
                }

                if (key === "MultiplierS6" && this.childs[key] instanceof LayoutSwitchElement) {
                    BaseView._multiplierS6 = this.childs[key];
                    BaseView._progressBarS6 = BaseView._multiplierS6.getChildElementByName(
                        "ProgressBar",
                    ) as ContainerElement;
                }
            }

            if (this._symbols == null) {
                if (DEBUG) {
                    this.log("ERROR no symbols");
                }
            }

            if (this._machine == null) {
                if (DEBUG) {
                    this.log("ERROR no machine");
                }
            }

            if (this._paylines == null) {
                if (DEBUG) {
                    this.log("WARNING no paylines");
                }
            }

            if (this._winAnimator == null) {
                if (DEBUG) {
                    this.log("WARNING no winAnimator");
                }
            }
        }

        this.setupGambleTransitionSceneElement();
        this._textContainerBottom = document.getElementById("textContainerBottom");
        this._textContainer = document.getElementById("textContainer");
    }

    protected layoutChanged() {
        if (ModuleManager.freespinsEnabled()) {
            this.showFsMessage();
        }
    }

    initReceived(controller: BaseController): void {
        if (DEBUG) {
            this.log("initReceived");
        }

        this._controller = controller;

        if (this._symbols != null) {
            if (this._machine != null) {
                if (GameManager.baseResponse != null) {
                    if (GameManager.baseResponse.initData != null && GameManager.baseResponse.initData.reels != null) {
                        this._machine.on(Machine.ReelStopped, this.onReelStopped.bind(this));
                        this._machine.on(Machine.MachineStopped, this.machineStopped.bind(this));
                        this._machine.initialize(
                            this._symbols,
                            GameManager.baseResponse.initData.reels,
                            this.childs["Teasing"],
                            this.childs["Background"],
                        );
                    }

                    if (
                        GameManager.baseResponse.screen != null &&
                        GameManager.baseResponse.screen.reels != null &&
                        GameManager.baseResponse.screen.reels.length !== 0
                    ) {
                        if (this._winAnimator != null) {
                            this._winAnimator.on(
                                WinAnimator.FinishWinCalculation,
                                this.finishWinCalculation.bind(this),
                            );
                            this._winAnimator.on(WinAnimator.FinishWinAnimator, this.finishWinAnimator.bind(this));
                            this._winAnimator.on(
                                WinAnimator.FinishQuickWinCalculation,
                                this.finishQuickWinCalculation.bind(this),
                            );
                            this._winAnimator.currentWinSymbolsContainer = this.childs["CurrentWinSymbolsContainer"];
                            this._winAnimator.initialize(
                                this._symbols,
                                this._paylines,
                                this._noWinContainer,
                                this._symbolsContainer,
                            );
                            this._winAnimator.showSymbols(GameManager.baseResponse.screen.reels);
                        }
                        this._machine.setStoppedSymbolsArray(GameManager.baseResponse.screen.reels);
                    } else {
                        if (DEBUG) {
                            this.log("ERROR no reels in response");
                        }
                    }
                } else {
                    if (DEBUG) {
                        this.log("ERROR no base reponse");
                    }
                }
            } else {
                if (DEBUG) {
                    this.log("ERROR no machine");
                }
            }
        } else {
            if (DEBUG) {
                this.log("ERROR no symbols");
            }
        }

        if (this._gambleTransition != null) {
            this._gambleTransition.goToCase("HIDE_GAMBLE_INIT");
        }

        if (ModuleManager.gambleActive()) {
            if (this.childs["Background"] !== null) this.childs["Background"].goToCase("BASE_WIN");
        }
    }

    disableLogo(): void {
        if (this._logo != null) {
            this.removeChild(this._logo);
            this._logo = null;
        }
    }

    initIntro(): void {
        StageController.baseStage = true;
        StageController.freespinStage = false;

        if (this._textContainerBottom != null) {
            this._textContainerBottom.classList.remove(HtmlController.DisabledClass);
        }

        if (this._textContainer != null) {
            this._textContainer.classList.remove(HtmlController.DisabledClass);
        }

        this.visible = true;

        if (this._gambleTransition != null) {
            this._gambleTransition.goToCase("SHOW_INIT");
        }
        const customBetData = DataManager.getCustomBetDataForCurrentBetAmount();

        BaseView.customCollectionBarsDataPreviousSpin = null;
        BaseView.customCollectionBarsDataPreviousSpinBonus = null;
        BaseView.customCollectionBarsData = DataManager.getCustomCollectionBarsData();

        BaseView._isInitIntro = true;
        BaseView.updateCurrentMultiplier(BaseView.customCollectionBarsData);
        BaseView.updateProgressBarsAndMultipliers(BaseView.customCollectionBarsData);

        if (ModuleManager.freespinsEnabled()) {
            PlayList.stop("SND_BaseLoop");
        } else if (GameManager.gameResponse.totalWin > 0) {
            //|| ModuleManager.freespinsEnabled()
            PlayList.loop("SND_BaseRecapLoop");
            PlayList.loop("SND_BaseBonusIdle");
            if (this.childs["Background"] !== null) this.childs["Background"].goToCase("BASE_WIN");
        } else {
            PlayList.loop("SND_BaseLoop");
        }

        if (ModuleManager.freespinsEnabled()) {
            this.showFsMessage();
        }

        this._winAnimator.showInitWins(GameManager.baseResponse.wins, GameManager.baseResponse.currentWin);
    }

    public static updateCurrentMultiplier(customCollectionBarsData: any): void {
        if (!Utils.isNullOrUndefined(customCollectionBarsData)) {
            let symbol6Count: number;
            let symbol5Count: number;
            let symbol4Count: number;
            let symbol3Count: number;
            let symbol2Count: number;
            let symbol1Count: number;

            const customData = customCollectionBarsData;
            const currentBet = BetManager.Bet;

            if (!Utils.isNullOrUndefined(customData)) {
                if (customData.hasOwnProperty("collectionBars")) {
                    let bonusArray = customData.collectionBars[currentBet];

                    if (!Utils.isNullOrUndefined(bonusArray)) {
                        symbol6Count = bonusArray[CustomUtils.SymbolWatermelon];
                        symbol5Count = bonusArray[CustomUtils.SymbolGrape];
                        symbol4Count = bonusArray[CustomUtils.SymbolPlum];
                        symbol3Count = bonusArray[CustomUtils.SymbolOrange];
                        symbol2Count = bonusArray[CustomUtils.SymbolLemon];
                        symbol1Count = bonusArray[CustomUtils.SymbolCherry];

                        BaseView._multiplierTimer.once(
                            FrameTimerEvents.Complete,
                            BaseView.setMultipliersCases.bind(
                                this,
                                symbol1Count,
                                symbol2Count,
                                symbol3Count,
                                symbol4Count,
                                symbol5Count,
                                symbol6Count,
                            ),
                        );
                        BaseView._multiplierTimer.start(0);
                    }
                }
            }
        }
    }

    public static updateProgressBarsAndMultipliers(customCollectionBarsData: any): void {
        BaseView.resetProgressBarsAndMultipliersCases();

        if (!Utils.isNullOrUndefined(customCollectionBarsData)) {
            let symbol6Count: number;
            let symbol5Count: number;
            let symbol4Count: number;
            let symbol3Count: number;
            let symbol2Count: number;
            let symbol1Count: number;
            let OLDsymbol6Count: number;
            let OLDsymbol5Count: number;
            let OLDsymbol4Count: number;
            let OLDsymbol3Count: number;
            let OLDsymbol2Count: number;
            let OLDsymbol1Count: number;

            const customData = customCollectionBarsData;
            const currentBet = BetManager.Bet;

            const customDataPreviousBet = BaseView.customCollectionBarsDataPreviousSpin;

            if (!Utils.isNullOrUndefined(customData)) {
                if (customData.hasOwnProperty("collectionBars")) {
                    let bonusArray = customData.collectionBars[currentBet];
                    if (!Utils.isNullOrUndefined(customDataPreviousBet)) {
                        let bonusArrayPreviousBet = customDataPreviousBet[currentBet];
                        if (!Utils.isNullOrUndefined(bonusArrayPreviousBet)) {
                            OLDsymbol6Count = bonusArrayPreviousBet[CustomUtils.SymbolWatermelon];
                            OLDsymbol5Count = bonusArrayPreviousBet[CustomUtils.SymbolGrape];
                            OLDsymbol4Count = bonusArrayPreviousBet[CustomUtils.SymbolPlum];
                            OLDsymbol3Count = bonusArrayPreviousBet[CustomUtils.SymbolOrange];
                            OLDsymbol2Count = bonusArrayPreviousBet[CustomUtils.SymbolLemon];
                            OLDsymbol1Count = bonusArrayPreviousBet[CustomUtils.SymbolCherry];
                        }
                    }

                    if (!Utils.isNullOrUndefined(bonusArray)) {
                        symbol6Count = bonusArray[CustomUtils.SymbolWatermelon];
                        symbol5Count = bonusArray[CustomUtils.SymbolGrape];
                        symbol4Count = bonusArray[CustomUtils.SymbolPlum];
                        symbol3Count = bonusArray[CustomUtils.SymbolOrange];
                        symbol2Count = bonusArray[CustomUtils.SymbolLemon];
                        symbol1Count = bonusArray[CustomUtils.SymbolCherry];

                        // first set progressbar cases - on Init Intro
                        if (BaseView._isInitIntro) {
                            BaseView.setProgressBarsCasesInitIntro(
                                symbol1Count,
                                OLDsymbol1Count,
                                symbol2Count,
                                OLDsymbol2Count,
                                symbol3Count,
                                OLDsymbol3Count,
                                symbol4Count,
                                OLDsymbol4Count,
                                symbol5Count,
                                OLDsymbol5Count,
                                symbol6Count,
                                OLDsymbol6Count,
                            );
                        }
                        // first set progressbar cases -  wins that dont increase multipliers
                        if (!BaseView._isInitIntro) {
                            BaseView.setProgressBarsCases(
                                symbol1Count,
                                OLDsymbol1Count,
                                symbol2Count,
                                OLDsymbol2Count,
                                symbol3Count,
                                OLDsymbol3Count,
                                symbol4Count,
                                OLDsymbol4Count,
                                symbol5Count,
                                OLDsymbol5Count,
                                symbol6Count,
                                OLDsymbol6Count,
                            );
                        }
                        // then set progressbars that increase with tween or go full with - increase multipliers
                        BaseView.setProgressBarsCasesWithTweens(
                            symbol1Count,
                            OLDsymbol1Count,
                            symbol2Count,
                            OLDsymbol2Count,
                            symbol3Count,
                            OLDsymbol3Count,
                            symbol4Count,
                            OLDsymbol4Count,
                            symbol5Count,
                            OLDsymbol5Count,
                            symbol6Count,
                            OLDsymbol6Count,
                        );

                        // if there are some old multipliers, set them first
                        BaseView.setOldMultipliersCases(
                            OLDsymbol1Count,
                            OLDsymbol2Count,
                            OLDsymbol3Count,
                            OLDsymbol4Count,
                            OLDsymbol5Count,
                            OLDsymbol6Count,
                        );

                        // then increase multipliers after delay
                        BaseView._multiplierTimer.once(
                            FrameTimerEvents.Complete,
                            BaseView.setMultipliersCasesPop.bind(
                                this,
                                symbol1Count,
                                symbol2Count,
                                symbol3Count,
                                symbol4Count,
                                symbol5Count,
                                symbol6Count,
                            ),
                        );
                        BaseView._multiplierTimer.start(50);

                        // after that finish progressbars that increased multipliers
                        BaseView._progressBarTimer.once(
                            FrameTimerEvents.Complete,
                            BaseView.setProgressBarsCasesWithTweensAfterMultiplier.bind(
                                this,
                                symbol1Count,
                                symbol2Count,
                                symbol3Count,
                                symbol4Count,
                                symbol5Count,
                                symbol6Count,
                            ),
                        );
                        BaseView._progressBarTimer.start(60);

                        // save custom data from previous spin
                        BaseView.customCollectionBarsDataPreviousSpin = customData.collectionBars;
                    }
                }
            }
        }
    }

    public static updateInstantMultiplierAndProgressBar(customCollectionBarsData: any): void {
        BaseView.resetProgressBarsAndMultipliersCases();

        if (!Utils.isNullOrUndefined(customCollectionBarsData)) {
            let symbol6Count: number;
            let symbol5Count: number;
            let symbol4Count: number;
            let symbol3Count: number;
            let symbol2Count: number;
            let symbol1Count: number;

            const customData = customCollectionBarsData;
            const currentBet = BetManager.Bet;
            //const customDataPreviousBet = BaseView.customCollectionBarsDataPreviousSpin;

            if (!Utils.isNullOrUndefined(customData)) {
                if (customData.hasOwnProperty("collectionBars")) {
                    let bonusArray = customData.collectionBars[currentBet];

                    symbol1Count = 0;
                    symbol2Count = 0;
                    symbol3Count = 0;
                    symbol4Count = 0;
                    symbol5Count = 0;
                    symbol6Count = 0;

                    if (!Utils.isNullOrUndefined(bonusArray)) {
                        symbol1Count = bonusArray[CustomUtils.SymbolCherry];
                        symbol2Count = bonusArray[CustomUtils.SymbolLemon];
                        symbol3Count = bonusArray[CustomUtils.SymbolOrange];
                        symbol4Count = bonusArray[CustomUtils.SymbolPlum];
                        symbol5Count = bonusArray[CustomUtils.SymbolGrape];
                        symbol6Count = bonusArray[CustomUtils.SymbolWatermelon];
                    }

                    // set Multiplier cases
                    BaseView.setMultipliersCases(
                        symbol1Count,
                        symbol2Count,
                        symbol3Count,
                        symbol4Count,
                        symbol5Count,
                        symbol6Count,
                    );

                    // set Progress bars cases
                    BaseView.setInstantProgressBarsCases(
                        symbol1Count,
                        symbol2Count,
                        symbol3Count,
                        symbol4Count,
                        symbol5Count,
                        symbol6Count,
                    );

                    // save custom data from previous spin
                    BaseView.customCollectionBarsDataPreviousSpin = customData.collectionBars;
                }
            }
        }
    }

    public static updateSpecificProgressBarsAndMultipliers(
        customCollectionBarsData: any,
        updateProgressId: number,
    ): void {
        if (!Utils.isNullOrUndefined(customCollectionBarsData)) {
            let symbol6Count: number;
            let symbol5Count: number;
            let symbol4Count: number;
            let symbol3Count: number;
            let symbol2Count: number;
            let symbol1Count: number;
            let OLDsymbol6Count: number;
            let OLDsymbol5Count: number;
            let OLDsymbol4Count: number;
            let OLDsymbol3Count: number;
            let OLDsymbol2Count: number;
            let OLDsymbol1Count: number;

            const customData = customCollectionBarsData;
            const currentBet = BetManager.Bet;
            const customDataPreviousBet = BaseView.customCollectionBarsDataPreviousSpin;

            if (!Utils.isNullOrUndefined(customData)) {
                if (customData.hasOwnProperty("collectionBars")) {
                    let bonusArray = customData.collectionBars[currentBet];

                    if (!Utils.isNullOrUndefined(customDataPreviousBet)) {
                        let bonusArrayPreviousBet = customDataPreviousBet[currentBet];
                        if (!Utils.isNullOrUndefined(bonusArrayPreviousBet)) {
                            OLDsymbol1Count = bonusArrayPreviousBet[CustomUtils.SymbolCherry];
                            OLDsymbol2Count = bonusArrayPreviousBet[CustomUtils.SymbolLemon];
                            OLDsymbol3Count = bonusArrayPreviousBet[CustomUtils.SymbolOrange];
                            OLDsymbol4Count = bonusArrayPreviousBet[CustomUtils.SymbolPlum];
                            OLDsymbol5Count = bonusArrayPreviousBet[CustomUtils.SymbolGrape];
                            OLDsymbol6Count = bonusArrayPreviousBet[CustomUtils.SymbolWatermelon];
                        }
                    }

                    if (!Utils.isNullOrUndefined(bonusArray)) {
                        symbol1Count = bonusArray[CustomUtils.SymbolCherry];
                        symbol2Count = bonusArray[CustomUtils.SymbolLemon];
                        symbol3Count = bonusArray[CustomUtils.SymbolOrange];
                        symbol4Count = bonusArray[CustomUtils.SymbolPlum];
                        symbol5Count = bonusArray[CustomUtils.SymbolGrape];
                        symbol6Count = bonusArray[CustomUtils.SymbolWatermelon];

                        // then set progressbars that increase with tween or go full with - increase multipliers
                        BaseView.setSpecificProgressBarsCasesWithTweens(
                            symbol1Count,
                            OLDsymbol1Count,
                            symbol2Count,
                            OLDsymbol2Count,
                            symbol3Count,
                            OLDsymbol3Count,
                            symbol4Count,
                            OLDsymbol4Count,
                            symbol5Count,
                            OLDsymbol5Count,
                            symbol6Count,
                            OLDsymbol6Count,
                            updateProgressId,
                        );

                        // if there are some old multipliers, set them first
                        BaseView.setSpecificOldMultipliersCases(
                            OLDsymbol1Count,
                            OLDsymbol2Count,
                            OLDsymbol3Count,
                            OLDsymbol4Count,
                            OLDsymbol5Count,
                            OLDsymbol6Count,
                            updateProgressId,
                        );

                        // then increase multipliers after delay
                        BaseView._multiplierTimer.once(
                            FrameTimerEvents.Complete,
                            BaseView.setSpecificMultipliersCasesPop.bind(
                                this,
                                symbol1Count,
                                symbol2Count,
                                symbol3Count,
                                symbol4Count,
                                symbol5Count,
                                symbol6Count,
                                updateProgressId,
                            ),
                        );
                        BaseView._multiplierTimer.start(50);

                        // after that finish progressbars that increased multipliers
                        BaseView._progressBarTimer.once(
                            FrameTimerEvents.Complete,
                            BaseView.setSpecificProgressBarsCasesWithTweensAfterMultiplier.bind(
                                this,
                                symbol1Count,
                                symbol2Count,
                                symbol3Count,
                                symbol4Count,
                                symbol5Count,
                                symbol6Count,
                                updateProgressId,
                            ),
                        );
                        BaseView._progressBarTimer.start(60);

                        // if multiplier is increased twice pop another time
                        if (BaseView["_isMultipliedTwiceS" + updateProgressId]) {
                            BaseView._multiplierTwiceTimer.once(
                                FrameTimerEvents.Complete,
                                BaseView.setSpecificMultipliersCasesPopTwice.bind(
                                    this,
                                    symbol1Count,
                                    symbol2Count,
                                    symbol3Count,
                                    symbol4Count,
                                    symbol5Count,
                                    symbol6Count,
                                    updateProgressId,
                                ),
                            );
                            BaseView._multiplierTwiceTimer.start(115);

                            // after finishing second pop finish progressbars that increased multiplier
                            BaseView._progressBarTwiceTimer.once(
                                FrameTimerEvents.Complete,
                                BaseView.setSpecificProgressBarsCasesWithTweensAfterMultiplierTwice.bind(
                                    this,
                                    symbol1Count,
                                    symbol2Count,
                                    symbol3Count,
                                    symbol4Count,
                                    symbol5Count,
                                    symbol6Count,
                                    updateProgressId,
                                ),
                            );
                            BaseView._progressBarTwiceTimer.start(135);
                        }
                    }
                }
            }
        }
    }

    public static resetProgressBarsAndMultipliersCases(): void {
        for (let j = 1; j <= 6; j++) {
            if (!Utils.isNullOrUndefined(BaseView["_progressBarS" + j])) {
                for (let i = 0; i < 10; i++) {
                    BaseView["_progressBarS" + j].childs["Selection" + i].visible = false;
                }
            }

            if (!Utils.isNullOrUndefined(BaseView["_multiplierS" + j])) {
                BaseView["_multiplierS" + j].goToCase("RESET");
            }
        }
    }

    public static setOldMultipliersCases(
        OLDsymbol1Count: number,
        OLDsymbol2Count: number,
        OLDsymbol3Count: number,
        OLDsymbol4Count: number,
        OLDsymbol5Count: number,
        OLDsymbol6Count: number,
    ): void {
        for (let j = 1; j <= 6; j++) {
            if (!Utils.isNullOrUndefined(BaseView["_multiplierS" + j])) {
                BaseView["_multiplierS" + j].goToCase("EMPTY");
            }

            let OLDsymbolCount = "OLDsymbol" + j + "Count";
            OLDsymbolCount = eval(OLDsymbolCount);

            if (+OLDsymbolCount > 9 && +OLDsymbolCount <= 99) {
                if (!Utils.isNullOrUndefined(BaseView["_multiplierS" + j])) {
                    BaseView["_multiplierS" + j].goToCase("BASE");
                }
                let multiplier = String(OLDsymbolCount)[0];
                multiplier = String(parseInt(multiplier) + 1);

                let numberElement = BaseView["_multiplierS" + j].getChildElementByName(
                    "FeatureCounter",
                ) as ImageTextElement;
                if (!Utils.isNullOrUndefined(numberElement)) {
                    numberElement.setText("x" + multiplier.toString());
                }
            } else if (+OLDsymbolCount > 99) {
                if (!Utils.isNullOrUndefined(BaseView["_multiplierS" + j])) {
                    BaseView["_multiplierS" + j].goToCase("BASE");
                }
                let multiplier: string = OLDsymbolCount.toString().substring(0, 2);
                multiplier = String(parseInt(multiplier) + 1);

                let numberElement = BaseView["_multiplierS" + j].getChildElementByName(
                    "FeatureCounter",
                ) as ImageTextElement;
                if (!Utils.isNullOrUndefined(numberElement)) {
                    numberElement.setText("x" + multiplier.toString());
                }
            }
        }
    }

    public static setSpecificOldMultipliersCases(
        OLDsymbol1Count: number,
        OLDsymbol2Count: number,
        OLDsymbol3Count: number,
        OLDsymbol4Count: number,
        OLDsymbol5Count: number,
        OLDsymbol6Count: number,
        updateProgressId: number,
    ): void {
        /*if (!Utils.isNullOrUndefined(BaseView['_multiplierS' + updateProgressId])) {
            BaseView['_multiplierS' + updateProgressId].goToCase('EMPTY');
        }*/

        let OLDsymbolCount = "OLDsymbol" + updateProgressId + "Count";
        OLDsymbolCount = eval(OLDsymbolCount);

        if (+OLDsymbolCount > 9 && +OLDsymbolCount <= 99) {
            let multiplier = String(OLDsymbolCount)[0];
            multiplier = String(parseInt(multiplier) + 1);

            let multiplierElement = BaseView["_multiplierS" + updateProgressId];
            if (!Utils.isNullOrUndefined(multiplierElement)) {
                multiplierElement.goToCase("BASE");

                let numberElement = multiplierElement.getChildElementByName("FeatureCounter") as ImageTextElement;
                if (!Utils.isNullOrUndefined(numberElement)) {
                    numberElement.setText("x" + multiplier.toString());
                }
            }
        } else if (+OLDsymbolCount > 99) {
            let multiplier: string = OLDsymbolCount.toString().substring(0, 2);
            multiplier = String(parseInt(multiplier) + 1);

            let multiplierElement = BaseView["_multiplierS" + updateProgressId];
            if (!Utils.isNullOrUndefined(multiplierElement)) {
                multiplierElement.goToCase("BASE");

                let numberElement = multiplierElement.getChildElementByName("FeatureCounter") as ImageTextElement;
                if (!Utils.isNullOrUndefined(numberElement)) {
                    numberElement.setText("x" + multiplier.toString());
                }
            }
        }
    }

    public static setMultipliersCases(
        symbol1Count: number,
        symbol2Count: number,
        symbol3Count: number,
        symbol4Count: number,
        symbol5Count: number,
        symbol6Count: number,
    ): void {
        BaseView._multiplierTimer.stop();

        for (let j = 1; j <= 6; j++) {
            let multiplierElement = BaseView["_multiplierS" + j];
            if (!Utils.isNullOrUndefined(multiplierElement)) {
                multiplierElement.goToCase("RESET");
            }

            let symbolCountVar = "symbol" + j + "Count";
            let symbolCount = eval(symbolCountVar);

            if (+symbolCount <= 9) {
                if (!Utils.isNullOrUndefined(multiplierElement)) {
                    multiplierElement.goToCase("BASE");

                    let numberElement = multiplierElement.getChildElementByName("FeatureCounter") as ImageTextElement;
                    if (!Utils.isNullOrUndefined(numberElement)) {
                        numberElement.setText("x1");
                    }
                }
            } else if (+symbolCount > 9 && +symbolCount <= 99) {
                let multiplier = String(symbolCount)[0];
                multiplier = String(parseInt(multiplier) + 1);

                if (!Utils.isNullOrUndefined(multiplierElement)) {
                    multiplierElement.goToCase("BASE");

                    let numberElement = multiplierElement.getChildElementByName("FeatureCounter") as ImageTextElement;
                    if (!Utils.isNullOrUndefined(numberElement)) {
                        numberElement.setText("x" + multiplier.toString());
                    }
                }
            } else if (+symbolCount > 99) {
                let multiplier: string = symbolCount.toString().substring(0, 2);
                multiplier = String(parseInt(multiplier) + 1);

                if (!Utils.isNullOrUndefined(multiplierElement)) {
                    multiplierElement.goToCase("BASE");

                    let numberElement = multiplierElement.getChildElementByName("FeatureCounter") as ImageTextElement;
                    if (!Utils.isNullOrUndefined(numberElement)) {
                        numberElement.setText("x" + multiplier.toString());
                    }
                }
            }
        }
    }

    public static setMultipliersCasesPop(
        symbol1Count: number,
        symbol2Count: number,
        symbol3Count: number,
        symbol4Count: number,
        symbol5Count: number,
        symbol6Count: number,
    ): void {
        BaseView._multiplierTimer.stop();

        for (let j = 1; j <= 6; j++) {
            let symbolCount = "symbol" + j + "Count";
            symbolCount = eval(symbolCount);

            if (BaseView["_isMultipliedS" + j]) {
                if (+symbolCount > 9 && +symbolCount <= 99) {
                    if (!Utils.isNullOrUndefined(BaseView["_multiplierS" + j])) {
                        BaseView["_multiplierS" + j].goToCase("BASE");
                        BaseView["_multiplierS" + j].goToCase("BASE_POP");
                    }
                    let multiplier = String(symbolCount)[0];
                    multiplier = String(parseInt(multiplier) + 1);

                    let numberElement = BaseView["_multiplierS" + j].getChildElementByName(
                        "FeatureCounter",
                    ) as ImageTextElement;
                    if (!Utils.isNullOrUndefined(numberElement)) {
                        numberElement.setText("x" + multiplier.toString());
                    }
                } else if (+symbolCount > 99) {
                    if (!Utils.isNullOrUndefined(BaseView["_multiplierS" + j])) {
                        BaseView["_multiplierS" + j].goToCase("BASE");
                        BaseView["_multiplierS" + j].goToCase("BASE_POP");
                    }
                    let multiplier: string = symbolCount.toString().substring(0, 2);
                    multiplier = String(parseInt(multiplier) + 1);

                    let numberElement = BaseView["_multiplierS" + j].getChildElementByName(
                        "FeatureCounter",
                    ) as ImageTextElement;
                    if (!Utils.isNullOrUndefined(numberElement)) {
                        numberElement.setText("x" + multiplier.toString());
                    }
                }
            }
        }
    }

    public static setSpecificMultipliersCasesPop(
        symbol1Count: number,
        symbol2Count: number,
        symbol3Count: number,
        symbol4Count: number,
        symbol5Count: number,
        symbol6Count: number,
        updateProgressId: number,
    ): void {
        BaseView._multiplierTimer.stop();

        let symbolCount = "symbol" + updateProgressId + "Count";
        symbolCount = eval(symbolCount);

        if (BaseView["_isMultipliedS" + updateProgressId]) {
            if (+symbolCount > 9 && +symbolCount <= 99) {
                if (!Utils.isNullOrUndefined(BaseView["_multiplierS" + updateProgressId])) {
                    BaseView["_multiplierS" + updateProgressId].goToCase("BASE");
                    BaseView["_multiplierS" + updateProgressId].goToCase("BASE_POP");
                }
                let multiplier = String(symbolCount)[0];
                multiplier = String(parseInt(multiplier) + 1);

                if (BaseView["_isMultipliedTwiceS" + updateProgressId]) {
                    multiplier = String(parseInt(multiplier) - 1);
                }

                PlayList.play("SND_BaseMultiplierRise");

                let numberElement = BaseView["_multiplierS" + updateProgressId].getChildElementByName(
                    "FeatureCounter",
                ) as ImageTextElement;
                if (!Utils.isNullOrUndefined(numberElement)) {
                    numberElement.setText("x" + multiplier.toString());
                }
            } else if (+symbolCount > 99) {
                if (!Utils.isNullOrUndefined(BaseView["_multiplierS" + updateProgressId])) {
                    BaseView["_multiplierS" + updateProgressId].goToCase("BASE");
                    BaseView["_multiplierS" + updateProgressId].goToCase("BASE_POP");
                }
                let multiplier: string = symbolCount.toString().substring(0, 2);
                multiplier = String(parseInt(multiplier) + 1);

                if (BaseView["_isMultipliedTwiceS" + updateProgressId]) {
                    multiplier = String(parseInt(multiplier) - 1);
                }

                PlayList.play("SND_BaseMultiplierRise");

                let numberElement = BaseView["_multiplierS" + updateProgressId].getChildElementByName(
                    "FeatureCounter",
                ) as ImageTextElement;
                if (!Utils.isNullOrUndefined(numberElement)) {
                    numberElement.setText("x" + multiplier.toString());
                }
            }
        }
    }

    public static setSpecificMultipliersCasesPopTwice(
        symbol1Count: number,
        symbol2Count: number,
        symbol3Count: number,
        symbol4Count: number,
        symbol5Count: number,
        symbol6Count: number,
        updateProgressId: number,
    ): void {
        BaseView._multiplierTwiceTimer.stop();

        let symbolCount = "symbol" + updateProgressId + "Count";
        symbolCount = eval(symbolCount);

        if (BaseView["_isMultipliedS" + updateProgressId]) {
            if (+symbolCount > 9 && +symbolCount <= 99) {
                if (!Utils.isNullOrUndefined(BaseView["_multiplierS" + updateProgressId])) {
                    BaseView["_multiplierS" + updateProgressId].goToCase("BASE");
                    BaseView["_multiplierS" + updateProgressId].goToCase("BASE_POP");
                }
                let multiplier = String(symbolCount)[0];
                multiplier = String(parseInt(multiplier) + 1);

                PlayList.play("SND_BaseMultiplierRise");

                let numberElement = BaseView["_multiplierS" + updateProgressId].getChildElementByName(
                    "FeatureCounter",
                ) as ImageTextElement;
                if (!Utils.isNullOrUndefined(numberElement)) {
                    numberElement.setText("x" + multiplier.toString());
                }
            } else if (+symbolCount > 99) {
                if (!Utils.isNullOrUndefined(BaseView["_multiplierS" + updateProgressId])) {
                    BaseView["_multiplierS" + updateProgressId].goToCase("BASE");
                    BaseView["_multiplierS" + updateProgressId].goToCase("BASE_POP");
                }
                let multiplier: string = symbolCount.toString().substring(0, 2);
                multiplier = String(parseInt(multiplier) + 1);

                PlayList.play("SND_BaseMultiplierRise");

                let numberElement = BaseView["_multiplierS" + updateProgressId].getChildElementByName(
                    "FeatureCounter",
                ) as ImageTextElement;
                if (!Utils.isNullOrUndefined(numberElement)) {
                    numberElement.setText("x" + multiplier.toString());
                }
            }
        }
    }

    public static setProgressBarsCasesInitIntro(
        symbol1Count: number,
        OLDsymbol1Count: number,
        symbol2Count: number,
        OLDsymbol2Count: number,
        symbol3Count: number,
        OLDsymbol3Count: number,
        symbol4Count: number,
        OLDsymbol4Count: number,
        symbol5Count: number,
        OLDsymbol5Count: number,
        symbol6Count: number,
        OLDsymbol6Count: number,
    ): void {
        //set new cases
        for (let j = 1; j <= 6; j++) {
            let symbolCount = "symbol" + j + "Count";
            let OLDsymbolCount = "OLDsymbol" + j + "Count";
            symbolCount = eval(symbolCount);
            OLDsymbolCount = eval(OLDsymbolCount);

            if (OLDsymbolCount == undefined || +OLDsymbolCount == +symbolCount) {
                if (+symbolCount > 0 && +symbolCount <= 9) {
                    const progressBar = String(+symbolCount)[0];
                    if (!Utils.isNullOrUndefined(BaseView["_progressBarS" + j])) {
                        for (let i = 0; i < +progressBar; i++) {
                            BaseView["_progressBarS" + j].childs["Selection" + i].visible = true;
                            BaseView["_progressBarS" + j].childs["Selection" + i].alpha = 1;
                        }
                    }
                } else if (+symbolCount > 9 && +symbolCount <= 99) {
                    const progressBar = String(+symbolCount)[1];

                    if (!Utils.isNullOrUndefined(BaseView["_progressBarS" + j])) {
                        for (let i = 0; i < +progressBar; i++) {
                            BaseView["_progressBarS" + j].childs["Selection" + i].visible = true;
                            BaseView["_progressBarS" + j].childs["Selection" + i].alpha = 1;
                        }
                    }
                } else if (+symbolCount >= 100) {
                    const progressBar = String(+symbolCount)[2];

                    if (!Utils.isNullOrUndefined(BaseView["_progressBarS" + j])) {
                        for (let i = 0; i < +progressBar; i++) {
                            BaseView["_progressBarS" + j].childs["Selection" + i].visible = true;
                            BaseView["_progressBarS" + j].childs["Selection" + i].alpha = 1;
                        }
                    }
                }
            }
        }
    }

    public static setProgressBarsCases(
        symbol1Count: number,
        OLDsymbol1Count: number,
        symbol2Count: number,
        OLDsymbol2Count: number,
        symbol3Count: number,
        OLDsymbol3Count: number,
        symbol4Count: number,
        OLDsymbol4Count: number,
        symbol5Count: number,
        OLDsymbol5Count: number,
        symbol6Count: number,
        OLDsymbol6Count: number,
    ): void {
        BaseView._isMultipliedS1 = false;
        BaseView._isMultipliedS2 = false;
        BaseView._isMultipliedS3 = false;
        BaseView._isMultipliedS4 = false;
        BaseView._isMultipliedS5 = false;
        BaseView._isMultipliedS6 = false;
        //set new cases

        for (let j = 1; j <= 6; j++) {
            let symbolCount = "symbol" + j + "Count";
            let OLDsymbolCount = "OLDsymbol" + j + "Count";
            symbolCount = eval(symbolCount);
            OLDsymbolCount = eval(OLDsymbolCount);

            if (OLDsymbolCount == undefined) {
                if (+symbolCount > 0 && +symbolCount <= 9) {
                    const progressBar = String(+symbolCount)[0];

                    if (!Utils.isNullOrUndefined(BaseView["_progressBarS" + j])) {
                        for (let i = 0; i < +progressBar; i++) {
                            let selection = BaseView["_progressBarS" + j].childs["Selection" + i];
                            if (selection.visible == false) {
                                selection.visible = true;
                                Tweens.removeTweens(selection, "all");
                                Tweens.alphaTween(selection, { alphas: [0, 1], tweenDuration: 10, tweenDelay: 5 * i });
                            }
                        }
                    }
                } else if (+symbolCount > 9) {
                    for (let i = 0; i <= 9; i++) {
                        let selection = BaseView["_progressBarS" + j].childs["Selection" + i];
                        selection.visible = true;
                        Tweens.removeTweens(selection, "all");
                        Tweens.alphaTween(selection, { alphas: [0, 1], tweenDuration: 10, tweenDelay: 5 * i });
                    }
                    BaseView["_isMultipliedS" + j] = true;
                }
            } else if (OLDsymbolCount == symbolCount) {
                if (+symbolCount > 0 && +symbolCount <= 9) {
                    const progressBar = String(+symbolCount)[0];

                    if (!Utils.isNullOrUndefined(BaseView["_progressBarS" + j])) {
                        for (let i = 0; i < +progressBar; i++) {
                            let selection = BaseView["_progressBarS" + j].childs["Selection" + i];
                            selection.visible = true;
                            selection.alpha = 1;
                        }
                    }
                }
            }
        }
    }

    public static setProgressBarsCasesWithTweens(
        symbol1Count: number,
        OLDsymbol1Count: number,
        symbol2Count: number,
        OLDsymbol2Count: number,
        symbol3Count: number,
        OLDsymbol3Count: number,
        symbol4Count: number,
        OLDsymbol4Count: number,
        symbol5Count: number,
        OLDsymbol5Count: number,
        symbol6Count: number,
        OLDsymbol6Count: number,
    ): void {
        //compare multipliers and if is new bigger - fill all bars

        for (let j = 1; j <= 6; j++) {
            let symbolCount = "symbol" + j + "Count";
            let OLDsymbolCount = "OLDsymbol" + j + "Count";
            symbolCount = eval(symbolCount);
            OLDsymbolCount = eval(OLDsymbolCount);
            BaseView["_isMultipliedS" + j] = false;

            if (+symbolCount > 9 && +symbolCount <= 99) {
                let multiplier = String(symbolCount)[0];
                if (+OLDsymbolCount > 9) {
                    let OLDmultiplier = String(OLDsymbolCount)[0];
                    if (multiplier > OLDmultiplier) {
                        const progressBar = String(OLDsymbolCount)[1];

                        if (!Utils.isNullOrUndefined(BaseView["_progressBarS" + j])) {
                            for (let i = 0; i < +progressBar; i++) {
                                BaseView["_progressBarS" + j].childs["Selection" + i].visible = true;
                                BaseView["_progressBarS" + j].childs["Selection" + i].alpha = 1;
                            }

                            for (let i = +progressBar; i < 10; i++) {
                                BaseView["_progressBarS" + j].childs["Selection" + i].visible = true;
                                Tweens.removeTweens(BaseView["_progressBarS" + j].childs["Selection" + i], "all");
                                Tweens.alphaTween(BaseView["_progressBarS" + j].childs["Selection" + i], {
                                    alphas: [0, 1],
                                    tweenDuration: 10,
                                    tweenDelay: 5 * i,
                                });
                            }
                            BaseView["_isMultipliedS" + j] = true;
                        }
                    } else if (multiplier == OLDmultiplier) {
                        const OLDprogressBar = String(OLDsymbolCount)[1];
                        const progressBar = String(symbolCount)[1];

                        if (!Utils.isNullOrUndefined(BaseView["_progressBarS" + j])) {
                            for (let i = 0; i < +OLDprogressBar; i++) {
                                BaseView["_progressBarS" + j].childs["Selection" + i].visible = true;
                                BaseView["_progressBarS" + j].childs["Selection" + i].alpha = 1;
                            }

                            for (let i = +OLDprogressBar; i < +progressBar; i++) {
                                BaseView["_progressBarS" + j].childs["Selection" + i].visible = true;
                                Tweens.removeTweens(BaseView["_progressBarS" + j].childs["Selection" + i], "all");
                                Tweens.alphaTween(BaseView["_progressBarS" + j].childs["Selection" + i], {
                                    alphas: [0, 1],
                                    tweenDuration: 10,
                                    tweenDelay: 5 * i,
                                });
                            }
                        }
                    }
                } else if (+OLDsymbolCount >= 0 && +OLDsymbolCount < 10) {
                    const OLDprogressBar = String(OLDsymbolCount)[0];

                    if (!Utils.isNullOrUndefined(BaseView["_progressBarS" + j])) {
                        for (let i = 0; i < +OLDprogressBar; i++) {
                            BaseView["_progressBarS" + j].childs["Selection" + i].visible = true;
                            BaseView["_progressBarS" + j].childs["Selection" + i].alpha = 1;
                        }

                        for (let i = +OLDprogressBar; i < 10; i++) {
                            BaseView["_progressBarS" + j].childs["Selection" + i].visible = true;
                            Tweens.removeTweens(BaseView["_progressBarS" + j].childs["Selection" + i], "all");
                            Tweens.alphaTween(BaseView["_progressBarS" + j].childs["Selection" + i], {
                                alphas: [0, 1],
                                tweenDuration: 10,
                                tweenDelay: 5 * i,
                            });
                        }

                        BaseView["_isMultipliedS" + j] = true;
                    }
                }
            } else if (+symbolCount > 99) {
                let multiplier: string = symbolCount.toString().substring(0, 2);
                if (+OLDsymbolCount > 99) {
                    let OLDmultiplier: string = OLDsymbolCount.toString().substring(0, 2);
                    if (multiplier > OLDmultiplier) {
                        const progressBar = String(OLDsymbolCount)[2];

                        if (!Utils.isNullOrUndefined(BaseView["_progressBarS" + j])) {
                            for (let i = 0; i < +progressBar; i++) {
                                BaseView["_progressBarS" + j].childs["Selection" + i].visible = true;
                                BaseView["_progressBarS" + j].childs["Selection" + i].alpha = 1;
                            }

                            for (let i = +progressBar; i < 10; i++) {
                                BaseView["_progressBarS" + j].childs["Selection" + i].visible = true;
                                Tweens.removeTweens(BaseView["_progressBarS" + j].childs["Selection" + i], "all");
                                Tweens.alphaTween(BaseView["_progressBarS" + j].childs["Selection" + i], {
                                    alphas: [0, 1],
                                    tweenDuration: 10,
                                    tweenDelay: 5 * i,
                                });
                            }
                            BaseView["_isMultipliedS" + j] = true;
                        }
                    } else if (multiplier == OLDmultiplier) {
                        const OLDprogressBar = String(OLDsymbolCount)[2];
                        const progressBar = String(symbolCount)[2];

                        if (!Utils.isNullOrUndefined(BaseView["_progressBarS" + j])) {
                            for (let i = 0; i < +OLDprogressBar; i++) {
                                BaseView["_progressBarS" + j].childs["Selection" + i].visible = true;
                                BaseView["_progressBarS" + j].childs["Selection" + i].alpha = 1;
                            }

                            for (let i = +OLDprogressBar; i < +progressBar; i++) {
                                BaseView["_progressBarS" + j].childs["Selection" + i].visible = true;
                                Tweens.removeTweens(BaseView["_progressBarS" + j].childs["Selection" + i], "all");
                                Tweens.alphaTween(BaseView["_progressBarS" + j].childs["Selection" + i], {
                                    alphas: [0, 1],
                                    tweenDuration: 10,
                                    tweenDelay: 5 * i,
                                });
                            }
                        }
                    }
                } else if (+OLDsymbolCount >= 90 && +OLDsymbolCount < 99) {
                    const OLDprogressBar = String(OLDsymbolCount)[1];

                    if (!Utils.isNullOrUndefined(BaseView["_progressBarS" + j])) {
                        for (let i = 0; i < +OLDprogressBar; i++) {
                            BaseView["_progressBarS" + j].childs["Selection" + i].visible = true;
                            BaseView["_progressBarS" + j].childs["Selection" + i].alpha = 1;
                        }

                        for (let i = +OLDprogressBar; i < 10; i++) {
                            BaseView["_progressBarS" + j].childs["Selection" + i].visible = true;
                            Tweens.removeTweens(BaseView["_progressBarS" + j].childs["Selection" + i], "all");
                            Tweens.alphaTween(BaseView["_progressBarS" + j].childs["Selection" + i], {
                                alphas: [0, 1],
                                tweenDuration: 10,
                                tweenDelay: 5 * i,
                            });
                        }

                        BaseView["_isMultipliedS" + j] = true;
                    }
                }
            }
        }

        for (let j = 1; j <= 6; j++) {
            let symbolCount = "symbol" + j + "Count";
            let OLDsymbolCount = "OLDsymbol" + j + "Count";
            symbolCount = eval(symbolCount);
            OLDsymbolCount = eval(OLDsymbolCount);

            if (+symbolCount < 10) {
                if (+OLDsymbolCount > 0 && +OLDsymbolCount < +symbolCount) {
                    const OLDprogressBar = String(OLDsymbolCount)[0];
                    const progressBar = String(symbolCount)[0];

                    if (!Utils.isNullOrUndefined(BaseView["_progressBarS" + j])) {
                        for (let i = 0; i < +OLDprogressBar; i++) {
                            BaseView["_progressBarS" + j].childs["Selection" + i].visible = true;
                            BaseView["_progressBarS" + j].childs["Selection" + i].alpha = 1;
                        }

                        for (let i = +OLDprogressBar; i < +progressBar; i++) {
                            BaseView["_progressBarS" + j].childs["Selection" + i].visible = true;
                            Tweens.removeTweens(BaseView["_progressBarS" + j].childs["Selection" + i], "all");
                            Tweens.alphaTween(BaseView["_progressBarS" + j].childs["Selection" + i], {
                                alphas: [0, 1],
                                tweenDuration: 10,
                                tweenDelay: 5 * i,
                            });
                        }
                    }
                } else if (OLDsymbolCount == null || +OLDsymbolCount == 0) {
                    const progressBar = String(symbolCount)[0];
                    for (let i = 0; i < +progressBar; i++) {
                        if (BaseView["_progressBarS" + j].childs["Selection" + i].visible == false) {
                            BaseView["_progressBarS" + j].childs["Selection" + i].visible = true;
                            Tweens.removeTweens(BaseView["_progressBarS" + j].childs["Selection" + i], "all");
                            Tweens.alphaTween(BaseView["_progressBarS" + j].childs["Selection" + i], {
                                alphas: [0, 1],
                                tweenDuration: 10,
                                tweenDelay: 5 * i,
                            });
                        }
                    }
                }
            }
        }
    }

    public static setSpecificProgressBarsCasesWithTweens(
        symbol1Count: number,
        OLDsymbol1Count: number,
        symbol2Count: number,
        OLDsymbol2Count: number,
        symbol3Count: number,
        OLDsymbol3Count: number,
        symbol4Count: number,
        OLDsymbol4Count: number,
        symbol5Count: number,
        OLDsymbol5Count: number,
        symbol6Count: number,
        OLDsymbol6Count: number,
        updateProgressId: number,
    ): void {
        BaseView._isMultipliedS1 = false;
        BaseView._isMultipliedS2 = false;
        BaseView._isMultipliedS3 = false;
        BaseView._isMultipliedS4 = false;
        BaseView._isMultipliedS5 = false;
        BaseView._isMultipliedS6 = false;

        BaseView._isMultipliedTwiceS1 = false;
        BaseView._isMultipliedTwiceS2 = false;
        BaseView._isMultipliedTwiceS3 = false;
        BaseView._isMultipliedTwiceS4 = false;
        BaseView._isMultipliedTwiceS5 = false;
        BaseView._isMultipliedTwiceS6 = false;
        //compare multipliers and if is new bigger - fill all bars

        let symbolCount = "symbol" + updateProgressId + "Count";
        let OLDsymbolCount = "OLDsymbol" + updateProgressId + "Count";
        symbolCount = eval(symbolCount);
        OLDsymbolCount = eval(OLDsymbolCount);
        BaseView["_isMultipliedS" + updateProgressId] = false;

        if (+symbolCount > 9 && +symbolCount <= 99) {
            let multiplier = String(symbolCount)[0];
            if (+OLDsymbolCount > 9) {
                let OLDmultiplier = String(OLDsymbolCount)[0];
                if (multiplier > OLDmultiplier) {
                    const progressBar = String(OLDsymbolCount)[1];

                    if (!Utils.isNullOrUndefined(BaseView["_progressBarS" + updateProgressId])) {
                        for (let i = 0; i < +progressBar; i++) {
                            BaseView["_progressBarS" + updateProgressId].childs["Selection" + i].visible = true;
                            BaseView["_progressBarS" + updateProgressId].childs["Selection" + i].alpha = 1;
                        }

                        let selectionAddCount = 10 - +progressBar;
                        if (selectionAddCount >= 1 && selectionAddCount <= 3) {
                            PlayList.play("SND_BaseProgressBarCount01");
                        } else if (selectionAddCount >= 4 && selectionAddCount <= 6) {
                            PlayList.play("SND_BaseProgressBarCount02");
                        } else if (selectionAddCount >= 7) {
                            PlayList.play("SND_BaseProgressBarCount03");
                        }

                        for (let i = +progressBar; i < 10; i++) {
                            BaseView["_progressBarS" + updateProgressId].childs["Selection" + i].visible = true;
                            Tweens.removeTweens(
                                BaseView["_progressBarS" + updateProgressId].childs["Selection" + i],
                                "all",
                            );
                            Tweens.alphaTween(BaseView["_progressBarS" + updateProgressId].childs["Selection" + i], {
                                alphas: [0, 1],
                                tweenDuration: 10,
                                tweenDelay: 5 * i,
                            });
                        }
                        if (+multiplier > +OLDmultiplier + 1) {
                            BaseView["_isMultipliedTwiceS" + updateProgressId] = true;
                        }
                        BaseView["_isMultipliedS" + updateProgressId] = true;
                    }
                } else if (multiplier == OLDmultiplier) {
                    const OLDprogressBar = String(OLDsymbolCount)[1];
                    const progressBar = String(symbolCount)[1];

                    if (!Utils.isNullOrUndefined(BaseView["_progressBarS" + updateProgressId])) {
                        for (let i = 0; i < +OLDprogressBar; i++) {
                            BaseView["_progressBarS" + updateProgressId].childs["Selection" + i].visible = true;
                            BaseView["_progressBarS" + updateProgressId].childs["Selection" + i].alpha = 1;
                        }

                        let selectionAddCount = +progressBar - +OLDprogressBar;
                        if (selectionAddCount >= 1 && selectionAddCount <= 3) {
                            PlayList.play("SND_BaseProgressBarCount01");
                        } else if (selectionAddCount >= 4 && selectionAddCount <= 6) {
                            PlayList.play("SND_BaseProgressBarCount02");
                        } else if (selectionAddCount >= 7) {
                            PlayList.play("SND_BaseProgressBarCount03");
                        }

                        for (let i = +OLDprogressBar; i < +progressBar; i++) {
                            BaseView["_progressBarS" + updateProgressId].childs["Selection" + i].visible = true;
                            Tweens.removeTweens(
                                BaseView["_progressBarS" + updateProgressId].childs["Selection" + i],
                                "all",
                            );
                            Tweens.alphaTween(BaseView["_progressBarS" + updateProgressId].childs["Selection" + i], {
                                alphas: [0, 1],
                                tweenDuration: 10,
                                tweenDelay: 5 * i,
                            });
                        }
                    }
                }
            } else if (+OLDsymbolCount >= 0 && +OLDsymbolCount < 10) {
                const OLDprogressBar = String(OLDsymbolCount)[0];

                if (!Utils.isNullOrUndefined(BaseView["_progressBarS" + updateProgressId])) {
                    for (let i = 0; i < +OLDprogressBar; i++) {
                        BaseView["_progressBarS" + updateProgressId].childs["Selection" + i].visible = true;
                        BaseView["_progressBarS" + updateProgressId].childs["Selection" + i].alpha = 1;
                    }

                    let selectionAddCount = 10 - +OLDprogressBar;
                    if (selectionAddCount >= 1 && selectionAddCount <= 3) {
                        PlayList.play("SND_BaseProgressBarCount01");
                    } else if (selectionAddCount >= 4 && selectionAddCount <= 6) {
                        PlayList.play("SND_BaseProgressBarCount02");
                    } else if (selectionAddCount >= 7) {
                        PlayList.play("SND_BaseProgressBarCount03");
                    }

                    for (let i = +OLDprogressBar; i < 10; i++) {
                        BaseView["_progressBarS" + updateProgressId].childs["Selection" + i].visible = true;
                        Tweens.removeTweens(
                            BaseView["_progressBarS" + updateProgressId].childs["Selection" + i],
                            "all",
                        );
                        Tweens.alphaTween(BaseView["_progressBarS" + updateProgressId].childs["Selection" + i], {
                            alphas: [0, 1],
                            tweenDuration: 10,
                            tweenDelay: 5 * i,
                        });
                    }

                    if (+multiplier > 1) {
                        BaseView["_isMultipliedTwiceS" + updateProgressId] = true;
                    }
                    BaseView["_isMultipliedS" + updateProgressId] = true;
                }
            } else if (!Utils.isNullOrUndefined(+OLDsymbolCount)) {
                if (!Utils.isNullOrUndefined(BaseView["_progressBarS" + updateProgressId])) {
                    PlayList.play("SND_BaseProgressBarCount03");

                    for (let i = 0; i < 10; i++) {
                        BaseView["_progressBarS" + updateProgressId].childs["Selection" + i].visible = true;
                        Tweens.removeTweens(
                            BaseView["_progressBarS" + updateProgressId].childs["Selection" + i],
                            "all",
                        );
                        Tweens.alphaTween(BaseView["_progressBarS" + updateProgressId].childs["Selection" + i], {
                            alphas: [0, 1],
                            tweenDuration: 10,
                            tweenDelay: 5 * i,
                        });
                    }

                    BaseView["_isMultipliedS" + updateProgressId] = true;
                }
            }
        } else if (+symbolCount > 99) {
            let multiplier: string = symbolCount.toString().substring(0, 2);
            if (+OLDsymbolCount > 99) {
                let OLDmultiplier: string = OLDsymbolCount.toString().substring(0, 2);
                if (multiplier > OLDmultiplier) {
                    const progressBar = String(OLDsymbolCount)[2];

                    let selectionAddCount = 10 - +progressBar;
                    if (selectionAddCount >= 1 && selectionAddCount <= 3) {
                        PlayList.play("SND_BaseProgressBarCount01");
                    } else if (selectionAddCount >= 4 && selectionAddCount <= 6) {
                        PlayList.play("SND_BaseProgressBarCount02");
                    } else if (selectionAddCount >= 7) {
                        PlayList.play("SND_BaseProgressBarCount03");
                    }

                    if (!Utils.isNullOrUndefined(BaseView["_progressBarS" + updateProgressId])) {
                        for (let i = 0; i < +progressBar; i++) {
                            BaseView["_progressBarS" + updateProgressId].childs["Selection" + i].visible = true;
                            BaseView["_progressBarS" + updateProgressId].childs["Selection" + i].alpha = 1;
                        }

                        for (let i = +progressBar; i < 10; i++) {
                            BaseView["_progressBarS" + updateProgressId].childs["Selection" + i].visible = true;
                            Tweens.removeTweens(
                                BaseView["_progressBarS" + updateProgressId].childs["Selection" + i],
                                "all",
                            );
                            Tweens.alphaTween(BaseView["_progressBarS" + updateProgressId].childs["Selection" + i], {
                                alphas: [0, 1],
                                tweenDuration: 10,
                                tweenDelay: 5 * i,
                            });
                        }
                        if (+multiplier > +OLDmultiplier + 1) {
                            BaseView["_isMultipliedTwiceS" + updateProgressId] = true;
                        }
                        BaseView["_isMultipliedS" + updateProgressId] = true;
                    }
                } else if (multiplier == OLDmultiplier) {
                    const OLDprogressBar = String(OLDsymbolCount)[2];
                    const progressBar = String(symbolCount)[2];

                    if (!Utils.isNullOrUndefined(BaseView["_progressBarS" + updateProgressId])) {
                        for (let i = 0; i < +OLDprogressBar; i++) {
                            BaseView["_progressBarS" + updateProgressId].childs["Selection" + i].visible = true;
                            BaseView["_progressBarS" + updateProgressId].childs["Selection" + i].alpha = 1;
                        }

                        let selectionAddCount = +progressBar - +OLDprogressBar;
                        if (selectionAddCount >= 1 && selectionAddCount <= 3) {
                            PlayList.play("SND_BaseProgressBarCount01");
                        } else if (selectionAddCount >= 4 && selectionAddCount <= 6) {
                            PlayList.play("SND_BaseProgressBarCount02");
                        } else if (selectionAddCount >= 7) {
                            PlayList.play("SND_BaseProgressBarCount03");
                        }

                        for (let i = +OLDprogressBar; i < +progressBar; i++) {
                            BaseView["_progressBarS" + updateProgressId].childs["Selection" + i].visible = true;
                            Tweens.removeTweens(
                                BaseView["_progressBarS" + updateProgressId].childs["Selection" + i],
                                "all",
                            );
                            Tweens.alphaTween(BaseView["_progressBarS" + updateProgressId].childs["Selection" + i], {
                                alphas: [0, 1],
                                tweenDuration: 10,
                                tweenDelay: 5 * i,
                            });
                        }
                    }
                }
            } else if (+OLDsymbolCount >= 90 && +OLDsymbolCount < 99) {
                const OLDprogressBar = String(OLDsymbolCount)[1];

                if (!Utils.isNullOrUndefined(BaseView["_progressBarS" + updateProgressId])) {
                    for (let i = 0; i < +OLDprogressBar; i++) {
                        BaseView["_progressBarS" + updateProgressId].childs["Selection" + i].visible = true;
                        BaseView["_progressBarS" + updateProgressId].childs["Selection" + i].alpha = 1;
                    }

                    let selectionAddCount = 10 - +OLDprogressBar;
                    if (selectionAddCount >= 1 && selectionAddCount <= 3) {
                        PlayList.play("SND_BaseProgressBarCount01");
                    } else if (selectionAddCount >= 4 && selectionAddCount <= 6) {
                        PlayList.play("SND_BaseProgressBarCount02");
                    } else if (selectionAddCount >= 7) {
                        PlayList.play("SND_BaseProgressBarCount03");
                    }

                    for (let i = +OLDprogressBar; i < 10; i++) {
                        BaseView["_progressBarS" + updateProgressId].childs["Selection" + i].visible = true;
                        Tweens.removeTweens(
                            BaseView["_progressBarS" + updateProgressId].childs["Selection" + i],
                            "all",
                        );
                        Tweens.alphaTween(BaseView["_progressBarS" + updateProgressId].childs["Selection" + i], {
                            alphas: [0, 1],
                            tweenDuration: 10,
                            tweenDelay: 5 * i,
                        });
                    }

                    BaseView["_isMultipliedS" + updateProgressId] = true;
                }
            }
        }

        let symbolCount2 = "symbol" + updateProgressId + "Count";
        let OLDsymbolCount2 = "OLDsymbol" + updateProgressId + "Count";
        symbolCount2 = eval(symbolCount2);
        OLDsymbolCount2 = eval(OLDsymbolCount2);

        if (+symbolCount2 < 10) {
            if (+OLDsymbolCount2 > 0 && +OLDsymbolCount2 < +symbolCount2) {
                const OLDprogressBar = String(OLDsymbolCount2)[0];
                const progressBar = String(symbolCount2)[0];

                if (!Utils.isNullOrUndefined(BaseView["_progressBarS" + updateProgressId])) {
                    for (let i = 0; i < +OLDprogressBar; i++) {
                        BaseView["_progressBarS" + updateProgressId].childs["Selection" + i].visible = true;
                        BaseView["_progressBarS" + updateProgressId].childs["Selection" + i].alpha = 1;
                    }

                    let selectionAddCount = +progressBar - +OLDprogressBar;
                    if (selectionAddCount >= 1 && selectionAddCount <= 3) {
                        PlayList.play("SND_BaseProgressBarCount01");
                    } else if (selectionAddCount >= 4 && selectionAddCount <= 6) {
                        PlayList.play("SND_BaseProgressBarCount02");
                    } else if (selectionAddCount >= 7) {
                        PlayList.play("SND_BaseProgressBarCount03");
                    }

                    for (let i = +OLDprogressBar; i < +progressBar; i++) {
                        BaseView["_progressBarS" + updateProgressId].childs["Selection" + i].visible = true;
                        Tweens.removeTweens(
                            BaseView["_progressBarS" + updateProgressId].childs["Selection" + i],
                            "all",
                        );
                        Tweens.alphaTween(BaseView["_progressBarS" + updateProgressId].childs["Selection" + i], {
                            alphas: [0, 1],
                            tweenDuration: 10,
                            tweenDelay: 5 * i,
                        });
                    }
                }
            } else if (OLDsymbolCount2 == null || +OLDsymbolCount2 == 0) {
                const progressBar = String(symbolCount2)[0];

                let selectionAddCount = +progressBar;
                if (selectionAddCount >= 1 && selectionAddCount <= 3) {
                    PlayList.play("SND_BaseProgressBarCount01");
                } else if (selectionAddCount >= 4 && selectionAddCount <= 6) {
                    PlayList.play("SND_BaseProgressBarCount02");
                } else if (selectionAddCount >= 7) {
                    PlayList.play("SND_BaseProgressBarCount03");
                }

                for (let i = 0; i < +progressBar; i++) {
                    if (BaseView["_progressBarS" + updateProgressId].childs["Selection" + i].visible == false) {
                        BaseView["_progressBarS" + updateProgressId].childs["Selection" + i].visible = true;
                        Tweens.removeTweens(
                            BaseView["_progressBarS" + updateProgressId].childs["Selection" + i],
                            "all",
                        );
                        Tweens.alphaTween(BaseView["_progressBarS" + updateProgressId].childs["Selection" + i], {
                            alphas: [0, 1],
                            tweenDuration: 10,
                            tweenDelay: 5 * i,
                        });
                    }
                }
            }
        }
    }

    public static setProgressBarsCasesWithTweensAfterMultiplier(
        symbol1Count: number,
        symbol2Count: number,
        symbol3Count: number,
        symbol4Count: number,
        symbol5Count: number,
        symbol6Count: number,
    ): void {
        BaseView._progressBarTimer.stop();

        for (let j = 1; j <= 6; j++) {
            let symbolCount = "symbol" + j + "Count";
            symbolCount = eval(symbolCount);

            if (BaseView["_isMultipliedS" + j]) {
                if (+symbolCount > 9 && +symbolCount <= 99) {
                    if (!Utils.isNullOrUndefined(BaseView["_progressBarS" + j])) {
                        for (let i = 0; i < 10; i++) {
                            BaseView["_progressBarS" + j].childs["Selection" + i].visible = false;
                        }

                        const progressBar = String(symbolCount)[1];

                        if (!Utils.isNullOrUndefined(BaseView["_progressBarS" + j])) {
                            for (let i = 0; i < +progressBar; i++) {
                                BaseView["_progressBarS" + j].childs["Selection" + i].visible = true;
                                Tweens.removeTweens(BaseView["_progressBarS" + j].childs["Selection" + i], "all");
                                Tweens.alphaTween(BaseView["_progressBarS" + j].childs["Selection" + i], {
                                    alphas: [0, 1],
                                    tweenDuration: 10,
                                    tweenDelay: 5 * i,
                                });
                            }
                        }
                    }
                } else if (+symbolCount > 99) {
                    if (!Utils.isNullOrUndefined(BaseView["_progressBarS" + j])) {
                        for (let i = 0; i < 10; i++) {
                            BaseView["_progressBarS" + j].childs["Selection" + i].visible = false;
                        }

                        const progressBar = String(symbolCount)[2];

                        if (!Utils.isNullOrUndefined(BaseView["_progressBarS" + j])) {
                            for (let i = 0; i < +progressBar; i++) {
                                BaseView["_progressBarS" + j].childs["Selection" + i].visible = true;
                                Tweens.removeTweens(BaseView["_progressBarS" + j].childs["Selection" + i], "all");
                                Tweens.alphaTween(BaseView["_progressBarS" + j].childs["Selection" + i], {
                                    alphas: [0, 1],
                                    tweenDuration: 10,
                                    tweenDelay: 5 * i,
                                });
                            }
                        }
                    }
                }
            }
        }
    }

    public static setInstantProgressBarsCases(
        symbol1Count: number,
        symbol2Count: number,
        symbol3Count: number,
        symbol4Count: number,
        symbol5Count: number,
        symbol6Count: number,
    ): void {
        BaseView._progressBarTimer.stop();

        for (let j = 1; j <= 6; j++) {
            let symbolCount = "symbol" + j + "Count";
            symbolCount = eval(symbolCount);

            if (+symbolCount > 0 && +symbolCount < 10) {
                if (!Utils.isNullOrUndefined(BaseView["_progressBarS" + j])) {
                    for (let i = 0; i < 10; i++) {
                        BaseView["_progressBarS" + j].childs["Selection" + i].visible = false;
                    }

                    const progressBar = String(symbolCount)[0];

                    if (!Utils.isNullOrUndefined(BaseView["_progressBarS" + j])) {
                        for (let i = 0; i < +progressBar; i++) {
                            BaseView["_progressBarS" + j].childs["Selection" + i].visible = true;
                            BaseView["_progressBarS" + j].childs["Selection" + i].alpha = 1;
                        }
                    }
                }
            } else if (+symbolCount > 9 && +symbolCount <= 99) {
                if (!Utils.isNullOrUndefined(BaseView["_progressBarS" + j])) {
                    for (let i = 0; i < 10; i++) {
                        BaseView["_progressBarS" + j].childs["Selection" + i].visible = false;
                    }

                    const progressBar = String(symbolCount)[1];

                    if (!Utils.isNullOrUndefined(BaseView["_progressBarS" + j])) {
                        for (let i = 0; i < +progressBar; i++) {
                            BaseView["_progressBarS" + j].childs["Selection" + i].visible = true;
                            BaseView["_progressBarS" + j].childs["Selection" + i].alpha = 1;
                        }
                    }
                }
            } else if (+symbolCount > 99) {
                if (!Utils.isNullOrUndefined(BaseView["_progressBarS" + j])) {
                    for (let i = 0; i < 10; i++) {
                        BaseView["_progressBarS" + j].childs["Selection" + i].visible = false;
                    }

                    const progressBar = String(symbolCount)[2];

                    if (!Utils.isNullOrUndefined(BaseView["_progressBarS" + j])) {
                        for (let i = 0; i < +progressBar; i++) {
                            BaseView["_progressBarS" + j].childs["Selection" + i].visible = true;
                            BaseView["_progressBarS" + j].childs["Selection" + i].alpha = 1;
                        }
                    }
                }
            }
        }
    }

    public static setSpecificProgressBarsCasesWithTweensAfterMultiplier(
        symbol1Count: number,
        symbol2Count: number,
        symbol3Count: number,
        symbol4Count: number,
        symbol5Count: number,
        symbol6Count: number,
        updateProgressId: number,
    ): void {
        BaseView._progressBarTimer.stop();

        let symbolCount = "symbol" + updateProgressId + "Count";
        symbolCount = eval(symbolCount);

        if (BaseView["_isMultipliedS" + updateProgressId]) {
            if (!BaseView["_isMultipliedTwiceS" + updateProgressId]) {
                if (+symbolCount > 9 && +symbolCount <= 99) {
                    if (!Utils.isNullOrUndefined(BaseView["_progressBarS" + updateProgressId])) {
                        for (let i = 0; i < 10; i++) {
                            BaseView["_progressBarS" + updateProgressId].childs["Selection" + i].visible = false;
                        }

                        const progressBar = String(symbolCount)[1];

                        let selectionAddCount = +progressBar;
                        if (selectionAddCount >= 1 && selectionAddCount <= 3) {
                            PlayList.play("SND_BaseProgressBarCount01");
                        } else if (selectionAddCount >= 4 && selectionAddCount <= 6) {
                            PlayList.play("SND_BaseProgressBarCount02");
                        } else if (selectionAddCount >= 7) {
                            PlayList.play("SND_BaseProgressBarCount03");
                        }

                        if (!Utils.isNullOrUndefined(BaseView["_progressBarS" + updateProgressId])) {
                            for (let i = 0; i < +progressBar; i++) {
                                BaseView["_progressBarS" + updateProgressId].childs["Selection" + i].visible = true;
                                Tweens.removeTweens(
                                    BaseView["_progressBarS" + updateProgressId].childs["Selection" + i],
                                    "all",
                                );
                                Tweens.alphaTween(
                                    BaseView["_progressBarS" + updateProgressId].childs["Selection" + i],
                                    {
                                        alphas: [0, 1],
                                        tweenDuration: 10,
                                        tweenDelay: 5 * i,
                                    },
                                );
                            }
                        }
                    }
                } else if (+symbolCount > 99) {
                    if (!Utils.isNullOrUndefined(BaseView["_progressBarS" + updateProgressId])) {
                        for (let i = 0; i < 10; i++) {
                            BaseView["_progressBarS" + updateProgressId].childs["Selection" + i].visible = false;
                        }

                        const progressBar = String(symbolCount)[2];

                        let selectionAddCount = +progressBar;
                        if (selectionAddCount >= 1 && selectionAddCount <= 3) {
                            PlayList.play("SND_BaseProgressBarCount01");
                        } else if (selectionAddCount >= 4 && selectionAddCount <= 6) {
                            PlayList.play("SND_BaseProgressBarCount02");
                        } else if (selectionAddCount >= 7) {
                            PlayList.play("SND_BaseProgressBarCount03");
                        }

                        if (!Utils.isNullOrUndefined(BaseView["_progressBarS" + updateProgressId])) {
                            for (let i = 0; i < +progressBar; i++) {
                                BaseView["_progressBarS" + updateProgressId].childs["Selection" + i].visible = true;
                                Tweens.removeTweens(
                                    BaseView["_progressBarS" + updateProgressId].childs["Selection" + i],
                                    "all",
                                );
                                Tweens.alphaTween(
                                    BaseView["_progressBarS" + updateProgressId].childs["Selection" + i],
                                    {
                                        alphas: [0, 1],
                                        tweenDuration: 10,
                                        tweenDelay: 5 * i,
                                    },
                                );
                            }
                        }
                    }
                }
            } else {
                if (!Utils.isNullOrUndefined(BaseView["_progressBarS" + updateProgressId])) {
                    for (let i = 0; i < 10; i++) {
                        BaseView["_progressBarS" + updateProgressId].childs["Selection" + i].visible = false;
                    }

                    PlayList.play("SND_BaseProgressBarCount03");

                    if (!Utils.isNullOrUndefined(BaseView["_progressBarS" + updateProgressId])) {
                        for (let i = 0; i < 10; i++) {
                            BaseView["_progressBarS" + updateProgressId].childs["Selection" + i].visible = true;
                            Tweens.removeTweens(
                                BaseView["_progressBarS" + updateProgressId].childs["Selection" + i],
                                "all",
                            );
                            Tweens.alphaTween(BaseView["_progressBarS" + updateProgressId].childs["Selection" + i], {
                                alphas: [0, 1],
                                tweenDuration: 10,
                                tweenDelay: 5 * i,
                            });
                        }
                    }
                }
            }
        }
    }

    public static setSpecificProgressBarsCasesWithTweensAfterMultiplierTwice(
        symbol1Count: number,
        symbol2Count: number,
        symbol3Count: number,
        symbol4Count: number,
        symbol5Count: number,
        symbol6Count: number,
        updateProgressId: number,
    ): void {
        BaseView._progressBarTwiceTimer.stop();

        let symbolCount = "symbol" + updateProgressId + "Count";
        symbolCount = eval(symbolCount);

        if (BaseView["_isMultipliedS" + updateProgressId]) {
            if (+symbolCount > 9 && +symbolCount <= 99) {
                if (!Utils.isNullOrUndefined(BaseView["_progressBarS" + updateProgressId])) {
                    for (let i = 0; i < 10; i++) {
                        BaseView["_progressBarS" + updateProgressId].childs["Selection" + i].visible = false;
                    }

                    const progressBar = String(symbolCount)[1];

                    let selectionAddCount = +progressBar;
                    if (selectionAddCount >= 1 && selectionAddCount <= 3) {
                        PlayList.play("SND_BaseProgressBarCount01");
                    } else if (selectionAddCount >= 4 && selectionAddCount <= 6) {
                        PlayList.play("SND_BaseProgressBarCount02");
                    } else if (selectionAddCount >= 7) {
                        PlayList.play("SND_BaseProgressBarCount03");
                    }

                    if (!Utils.isNullOrUndefined(BaseView["_progressBarS" + updateProgressId])) {
                        for (let i = 0; i < +progressBar; i++) {
                            BaseView["_progressBarS" + updateProgressId].childs["Selection" + i].visible = true;
                            Tweens.removeTweens(
                                BaseView["_progressBarS" + updateProgressId].childs["Selection" + i],
                                "all",
                            );
                            Tweens.alphaTween(BaseView["_progressBarS" + updateProgressId].childs["Selection" + i], {
                                alphas: [0, 1],
                                tweenDuration: 10,
                                tweenDelay: 5 * i,
                            });
                        }
                    }
                }
            } else if (+symbolCount > 99) {
                if (!Utils.isNullOrUndefined(BaseView["_progressBarS" + updateProgressId])) {
                    for (let i = 0; i < 10; i++) {
                        BaseView["_progressBarS" + updateProgressId].childs["Selection" + i].visible = false;
                    }

                    const progressBar = String(symbolCount)[2];

                    let selectionAddCount = +progressBar;
                    if (selectionAddCount >= 1 && selectionAddCount <= 3) {
                        PlayList.play("SND_BaseProgressBarCount01");
                    } else if (selectionAddCount >= 4 && selectionAddCount <= 6) {
                        PlayList.play("SND_BaseProgressBarCount02");
                    } else if (selectionAddCount >= 7) {
                        PlayList.play("SND_BaseProgressBarCount03");
                    }

                    if (!Utils.isNullOrUndefined(BaseView["_progressBarS" + updateProgressId])) {
                        for (let i = 0; i < +progressBar; i++) {
                            BaseView["_progressBarS" + updateProgressId].childs["Selection" + i].visible = true;
                            Tweens.removeTweens(
                                BaseView["_progressBarS" + updateProgressId].childs["Selection" + i],
                                "all",
                            );
                            Tweens.alphaTween(BaseView["_progressBarS" + updateProgressId].childs["Selection" + i], {
                                alphas: [0, 1],
                                tweenDuration: 10,
                                tweenDelay: 5 * i,
                            });
                        }
                    }
                }
            }
        }
    }

    collect(): void {
        this._winAnimator.stopProgressbarsTimers();
        BaseView.updateInstantMultiplierAndProgressBar(BaseView.customCollectionBarsData);

        PlayList.stop("SND_BaseRecapLoop");
        PlayList.stop("SND_BaseBonusIdle");
        PlayList.loop("SND_BaseLoop");

        if (this.childs["Background"] !== null && this.childs["Background"].getCurrentCase() === "BASE_WIN") {
            this.childs["Background"].goToCase("TO_BASE");
        }

        this._winAnimator.reset(true);
    }

    spin(): void {
        this._winAnimator.stopProgressbarsTimers();
        BaseView.updateInstantMultiplierAndProgressBar(BaseView.customCollectionBarsData);

        PlayList.stop("SND_BaseRecapLoop");
        PlayList.stop("SND_BaseBonusIdle");
        PlayList.loop("SND_BaseLoop");

        if (this.childs["Background"] !== null && this.childs["Background"].getCurrentCase() === "BASE_WIN") {
            this.childs["Background"].goToCase("TO_BASE");
        }

        this._machine.spinStart(this._winAnimator.reset(true), true, this._winAnimator.currentSymbols);
    }

    stop(): void {
        this._machine.spinStop(null, true);
    }

    spinReceived(): void {
        DataManager.updateCustomBetsData(); // Custom for CollectEmFruits. Update "custom bets data".

        this._machine.setStoppedSymbolsArray(GameManager.baseResponse.screen.reels);
        this._machine.spinStop(null, false, true);
    }

    onReelStopped(): void {
        this._winAnimator.onReelStopped(
            GameManager.baseResponse.screen.reels,
            this._machine.actualReelStopped,
            this._gambleTransition,
        );
    }

    machineStopped(): void {
        if (GameManager.baseResponse.currentWin > 0) {
            if (this.childs["Background"] !== null) {
                this.childs["Background"].goToCase("BASE_WIN");
            }
        }

        if (
            this._textContainerBottom != null &&
            this._textContainerBottom.classList.contains(HtmlController.DisabledClass)
        ) {
            this._textContainerBottom.classList.remove(HtmlController.DisabledClass);
        }

        if (this._textContainer != null && this._textContainer.classList.contains(HtmlController.DisabledClass)) {
            this._textContainer.classList.remove(HtmlController.DisabledClass);
        }

        PlayList.loop("SND_BaseLoop");

        BaseView.customCollectionBarsData = DataManager.getCustomCollectionBarsData();
        BaseView._isInitIntro = false;

        this._wasFinished = false;
        this._controller.machineStopped();
        if (this._winAnimator.showWins(GameManager.baseResponse.wins, GameManager.baseResponse.currentWin)) {
            PlayList.stop("SND_BaseLoop");
        }
    }

    private finishQuickWinCalculation(): void {
        this._controller.quickWinCalculationFinished();
    }

    finishWinCalculation(): void {
        this._wasFinished = true;
        this._controller.winCalculationFinished();
    }

    finishWinAnimator(): void {
        if (!DataManager.Autoplay || ModuleManager.freespinsEnabled()) {
            PlayList.stop("SND_BaseLoop");

            if (ModuleManager.freespinsEnabled()) {
                // PlayList.loop('SND_FSWait');
            } else {
                PlayList.loop("SND_BaseRecapLoop");
                PlayList.loop("SND_BaseBonusIdle");
            }
        }

        if (BaseView.messages != null) {
            if (ModuleManager.freespinsEnabled()) {
                this.showFsMessage();
            }
        }
        this._controller.allWinsFinished();
    }

    public showFsMessage(): void {
        this._fsMessageTimer.stop();

        if (this._textContainerBottom != null) {
            this._textContainerBottom.classList.add(HtmlController.DisabledClass);
        }

        if (this._textContainer != null) {
            this._textContainer.classList.add(HtmlController.DisabledClass);
        }

        if (
            BaseView.messages != null &&
            ["SHOW_FS", "SHOW_FS_2", "SHOW_FS_END"].indexOf(BaseView.messages.getCurrentCase()) == -1
        ) {
            let freespinsCount = BaseView.messages.getChildElementByName("FreespinsCount") as ImageTextCounterElement;
            if (!Utils.isNullOrUndefined(freespinsCount) && freespinsCount instanceof ImageTextCounterElement) {
                freespinsCount.setText(GameManager.freespinsResponse.freeSpins.totalCount.toString());
            }
            BaseView.messages.goToCase("SHOW_FS");
        }

        PlayList.play("SND_FSWin");
        PlayList.loop("SND_FSWait", 120);
    }

    resetWinAnimator(): void {
        if (this.childs["Background"] !== null && this.childs["Background"].getCurrentCase() === "BASE_WIN")
            this.childs["Background"].goToCase("TO_BASE");
        this._winAnimator.reset(true);
    }

    intro(long: boolean = false): void {
        StageController.baseStage = true;
        StageController.freespinStage = false;
        BaseView._isInitIntro = false;

        if (this._textContainerBottom != null) {
            this._textContainerBottom.classList.remove(HtmlController.DisabledClass);
        }

        if (this._textContainer != null) {
            this._textContainer.classList.remove(HtmlController.DisabledClass);
        }

        this._winAnimator.stopProgressbarsTimers();

        this.allSwitchElementToCase("NORMAL");

        if (this.childs["Background"] !== null && this.childs["Background"].getCurrentCase() === "BASE_WIN") {
            this.childs["Background"].goToCase("TO_BASE");
        }

        if (GameManager.gameResponse.totalWin > 0) {
            //fs return
            PlayList.stop("SND_BaseLoop");
            PlayList.loop("SND_BaseRecapLoop");
            PlayList.loop("SND_BaseBonusIdle");
        } else {
            PlayList.stop("SND_BaseRecapLoop");
            PlayList.stop("SND_BaseBonusIdle");
            PlayList.loop("SND_BaseLoop");
        }

        if (long) {
            for (let j = 1; j <= 6; j++) {
                if (!Utils.isNullOrUndefined(BaseView["_multiplierS" + j])) {
                    let numberElement = BaseView["_multiplierS" + j].getChildElementByName(
                        "FeatureCounter",
                    ) as ImageTextElement;
                    if (!Utils.isNullOrUndefined(numberElement)) {
                        numberElement.setText("x1");
                    }
                }
            }

            if (FreespinsView.Transition != null) {
                this.addChild(FreespinsView.Transition);

                BaseView.customCollectionBarsData = DataManager.getCustomCollectionBarsData(); // ?
                BaseView.updateInstantMultiplierAndProgressBar(BaseView.customCollectionBarsData); // ?

                BaseView._isInitIntro = false;
                BaseView.customCollectionBarsData = null;
                BaseView.customCollectionBarsDataPreviousSpin = null;

                BaseView.resetProgressBarsAndMultipliersCases();
            }
            if (this._gambleTransition != null) {
                this._gambleTransition.setDynamicTime(0);
                this._gambleTransition.goToCase("SHOW");
            }
            this._introDelay.start(15);
        } else if (this._gambleTransition != null) {
            this._gambleTransition.setDynamicTime(0);
            this._gambleTransition.goToCase("SHOW");

            this._introDelay.start(15);
        } else {
            this.introFinished();
        }
    }

    introFinished(): void {
        if (ModuleManager.freespinsEnabled()) {
            if (BaseView.messages != null) {
                BaseView.messages.goToCase("SHOW_FS");
            }
        }

        this._controller.introFinished();
    }

    outro(): void {
        PlayList.stop("SND_BaseLoop");
        PlayList.stop("SND_BaseRecapLoop");
        PlayList.stop("SND_BaseBonusIdle");

        let wasFinished: boolean = this._wasFinished;
        let inHighWins: boolean = this._winAnimator.reset(true);

        clearTimeout(this._winAnimator.showTextUnderLogoDelay);

        if (ModuleManager.freespinsActive()) {
            if (FreespinsView.Transition != null) {
                this.addChild(FreespinsView.Transition);
                FreespinsView.Transition.goToCase("IN");

                PlayList.stop("SND_FSWait");
                PlayList.play("SND_FSTransIn");
            }

            if (
                !Utils.isNullOrUndefined(BaseView.messages) != null &&
                (BaseView.messages.getCurrentCase() == "SHOW_FS" ||
                    BaseView.messages.getCurrentCase() == "SHOW_FS_IDLE" ||
                    BaseView.messages.getCurrentCase() == "SHOW_FS_2")
            ) {
                BaseView.messages.goToCase("HIDE_FS_BASE");
            }

            this._outroDelay.start(15);
        } else {
            this._winAnimator.stopProgressbarsTimers();
            BaseView.updateInstantMultiplierAndProgressBar(BaseView.customCollectionBarsData);

            if (wasFinished) {
                this.outroStart();
            } else {
                if (inHighWins) {
                    PlayList.play("SND_GambleTransition");
                    this._gambleTransition.setDynamicTime(0);
                    this._gambleTransition.goToCase("HIDE_GAMBLE");
                    this._outroDelay.start(60);
                } else {
                    PlayList.play("SND_GambleTransition");
                    this._gambleTransition.setDynamicTime(0);
                    this._gambleTransition.goToCase("HIDE_GAMBLE");
                    this._outroDelay.start(30);
                }
            }
        }
    }

    outroStart(): void {
        if (ModuleManager.freespinsActive()) {
            this.outroFinished();
        } else {
            PlayList.play("SND_GambleTransition");
            this._gambleTransition.setDynamicTime(0);
            this._gambleTransition.goToCase("HIDE_GAMBLE");
            this._outroDelay.start(30);
        }
    }

    outroFinished(): void {
        this._controller.outroFinished();
        this._winAnimator.resetTimer();

        if (this._textContainerBottom != null) {
            this._textContainerBottom.classList.add(HtmlController.DisabledClass);
        }

        if (this._textContainer != null) {
            this._textContainer.classList.add(HtmlController.DisabledClass);
        }

        PlayList.stop("SND_BaseMultiplierRise");
        PlayList.stop("SND_BaseProgressBarCount01");
        PlayList.stop("SND_BaseProgressBarCount02");
        PlayList.stop("SND_BaseProgressBarCount03");
    }

    private allSwitchElementToCase(caseType: string): void {
        for (let key in this.childs) {
            if (this.childs[key] instanceof SwitchElement) {
                let elem: SwitchElement = this.childs[key];

                if (elem.hasCase(caseType)) {
                    elem.goToCase(caseType);
                }
            }
        }
    }

    disableBase(): void {
        this._winAnimator.resetTimer();
    }

    /**
     * Toggle all sounds used in base game (background, effects, .etc)
     * @param enable
     */
    toggleSounds(enable: boolean): void {
        if (enable) {
            PlayList.loop("SND_BaseLoop");
            PlayList.stop("SND_FSLoop");
        } else {
            PlayList.stop("SND_BaseLoop");
            PlayList.stop("SND_BaseRecapLoop");
            PlayList.stop("SND_BaseBonusIdle");
        }
    }

    /**
     * Switch symbols of winAnimator by selected bet.
     */
    updateReelsByBetChange(init = false) {
        const customBetData = DataManager.getCustomBetDataForCurrentBetAmount();
        if (!Utils.isNullOrUndefined(customBetData)) {
            if (init || this.currentBet != customBetData.betAmount) {
                this.currentBet = customBetData.betAmount;

                // Update reels symbols
                this._winAnimator.showSymbols(customBetData.reelsData);
            }
        }
    }

    private setupGambleTransitionSceneElement(): void {
        this._gambleTransitionSceneHolder.addElement({
            element: this.childs["Reels"],
            parent: this,
            index: this.getChildIndex(this.childs["Reels"]),
        });
        this._gambleTransitionSceneHolder.addElement({
            element: this.childs["Lines"],
            parent: this,
            index: this.getChildIndex(this.childs["Lines"]),
        });
        this._gambleTransitionSceneHolder.addElement({
            element: this.childs["MultiplierS1"],
            parent: this,
            index: this.getChildIndex(this.childs["MultiplierS1"]),
        });
        this._gambleTransitionSceneHolder.addElement({
            element: this.childs["MultiplierS2"],
            parent: this,
            index: this.getChildIndex(this.childs["MultiplierS2"]),
        });
        this._gambleTransitionSceneHolder.addElement({
            element: this.childs["MultiplierS3"],
            parent: this,
            index: this.getChildIndex(this.childs["MultiplierS3"]),
        });
        this._gambleTransitionSceneHolder.addElement({
            element: this.childs["MultiplierS4"],
            parent: this,
            index: this.getChildIndex(this.childs["MultiplierS4"]),
        });
        this._gambleTransitionSceneHolder.addElement({
            element: this.childs["MultiplierS5"],
            parent: this,
            index: this.getChildIndex(this.childs["MultiplierS5"]),
        });
        this._gambleTransitionSceneHolder.addElement({
            element: this.childs["MultiplierS6"],
            parent: this,
            index: this.getChildIndex(this.childs["MultiplierS6"]),
        });
        this._gambleTransitionSceneHolder.addElement({
            element: this.childs["Machine"],
            parent: this,
            index: this.getChildIndex(this.childs["Machine"]),
        });
        this._gambleTransitionSceneHolder.addElement({
            element: this.childs["NoWinContainer"],
            parent: this,
            index: this.getChildIndex(this.childs["NoWinContainer"]),
        });
        this._gambleTransitionSceneHolder.addElement({
            element: this.childs["SymbolsContainer"],
            parent: this,
            index: this.getChildIndex(this.childs["SymbolsContainer"]),
        });
        this._gambleTransitionSceneHolder.addElement({
            element: this.childs["Paylines"],
            parent: this,
            index: this.getChildIndex(this.childs["Paylines"]),
        });
        this._gambleTransitionSceneHolder.addElement({
            element: this.childs["Teasing"],
            parent: this,
            index: this.getChildIndex(this.childs["Teasing"]),
        });
        this._gambleTransitionSceneHolder.addElement({
            element: this.childs["CurrentWinSymbolsContainer"],
            parent: this,
            index: this.getChildIndex(this.childs["CurrentWinSymbolsContainer"]),
        });
        this._gambleTransitionSceneHolder.addElement({
            element: this.childs["Lights"],
            parent: this,
            index: this.getChildIndex(this.childs["Lights"]),
        });
        this._gambleTransitionSceneHolder.addElement({
            element: this.childs["Roof"],
            parent: this,
            index: this.getChildIndex(this.childs["Roof"]),
        });
        this._gambleTransitionSceneHolder.addElement({
            element: this.childs["WinAnimator"],
            parent: this,
            index: this.getChildIndex(this.childs["WinAnimator"]),
        });
        this._gambleTransitionSceneHolder.addElement({
            element: this.childs["Messages"],
            parent: this,
            index: this.getChildIndex(this.childs["Messages"]),
        });
    }
}



/** WEBPACK FOOTER **
 ** ./Engine/Game/GameComponents/Base/BaseView.ts
 **/